import { useEffect, useRef, useState } from 'react';
import { Play, Pause, RotateCcw, SkipForward } from 'lucide-react';

/**
 * Larger, more legible game-timer used for both Балаган (group discussion)
 * and individual speaker speeches.
 *
 * Props:
 *  - duration:    seconds for one cycle (e.g. 120 for Балаган, 60 for speech)
 *  - seat:        optional speaker seat number (shown in the badge)
 *  - label:       big label shown next to the ring (e.g. "Балаган", "Speech")
 *  - sublabel:    small caption shown under the label
 *  - accentColor: ring + button accent (hex). Defaults to red.
 *  - autoStart:   start counting down on mount
 *  - onComplete:  fires when the timer hits 0
 *  - onNext:      optional — if provided, renders a "Next" button (used to
 *                 advance to the next speaker without resetting state).
 *  - testid:      data-testid root
 */
export default function SpeechTimer({
  duration = 60,
  seat = null,
  label = 'Speech',
  sublabel = '',
  accentColor = '#DC2626',
  autoStart = false,
  onComplete,
  onNext,
  testid = 'speech-timer',
}) {
  const [remaining, setRemaining] = useState(duration);
  const [running, setRunning] = useState(autoStart);
  const intervalRef = useRef(null);

  // Reset whenever the underlying timer configuration changes
  useEffect(() => {
    setRemaining(duration);
    setRunning(autoStart);
  }, [duration, seat, autoStart]);

  useEffect(() => {
    if (running) {
      intervalRef.current = setInterval(() => {
        setRemaining((r) => {
          if (r <= 1) {
            clearInterval(intervalRef.current);
            setRunning(false);
            onComplete && onComplete();
            return 0;
          }
          return r - 1;
        });
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  const mins = String(Math.floor(remaining / 60)).padStart(1, '0');
  const secs = String(remaining % 60).padStart(2, '0');
  const pct = (remaining / duration) * 100;
  const ringColor = remaining <= 5 ? '#EF4444' : remaining <= 15 ? '#F59E0B' : accentColor;
  const radius = 28;
  const circumference = 2 * Math.PI * radius;

  return (
    <div
      className="flex items-center gap-4 px-5 py-4 border border-white/10 bg-black/60"
      style={{ borderColor: `${accentColor}30` }}
      data-testid={testid}
    >
      <div className="relative w-20 h-20 shrink-0">
        <svg viewBox="0 0 64 64" className="w-20 h-20 -rotate-90">
          <circle cx="32" cy="32" r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
          <circle
            cx="32"
            cy="32"
            r={radius}
            fill="none"
            stroke={ringColor}
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={`${(pct / 100) * circumference} ${circumference}`}
            style={{ transition: 'stroke-dasharray 0.3s linear, stroke 0.3s ease' }}
          />
        </svg>
        <div
          className="absolute inset-0 flex items-center justify-center text-2xl font-mono tabular-nums"
          style={{ color: '#F5F5F5' }}
        >
          {mins}:{secs}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-serif text-xl text-[#F5F5F5] truncate">{label}</span>
        </div>
        {sublabel && <p className="text-xs text-[#A3A3A3] mt-1 truncate">{sublabel}</p>}
      </div>

      <div className="flex gap-1.5">
        <button
          onClick={() => setRunning((r) => !r)}
          data-testid={`${testid}-toggle`}
          className="p-2.5 border border-white/10 hover:border-white/30 transition-colors"
          aria-label={running ? 'Pause' : 'Play'}
        >
          {running ? (
            <Pause size={18} className="text-[#F5F5F5]" />
          ) : (
            <Play size={18} style={{ color: accentColor }} />
          )}
        </button>
        <button
          onClick={() => {
            setRemaining(duration);
            setRunning(false);
          }}
          data-testid={`${testid}-reset`}
          className="p-2.5 border border-white/10 hover:border-white/30 transition-colors"
          aria-label="Reset"
        >
          <RotateCcw size={18} className="text-[#A3A3A3]" />
        </button>
        {onNext && (
          <button
            onClick={onNext}
            data-testid={`${testid}-next`}
            className="p-2.5 border hover:opacity-90 transition-colors"
            style={{ borderColor: `${accentColor}66`, color: accentColor, background: `${accentColor}10` }}
            aria-label="Next"
          >
            <SkipForward size={18} />
          </button>
        )}
      </div>
    </div>
  );
}
